import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CornerDownRight, FilePenLine } from 'lucide-react';

import { updateQuotation } from '#/api/crmMgmt/quotation/update';
import { PERM } from '#/config/permissions';
import { Button } from '#/modules/commonComponents/button';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import QuotationInternalRemarkDialog from '#/modules/crmMgmt/quotation/components/QuotationInternalRemarkDialog';
import {
  getQuotationApiErrorMessage,
  isQuotationVersionConflict,
} from '#/modules/crmMgmt/quotation/helper/apiError';
import { formatQuotationDecimal } from '#/modules/crmMgmt/quotation/helper/quotationFormat';
import { buildQuotationInternalRemarkPatch } from '#/modules/crmMgmt/quotation/helper/quotationLines';
import { canUpdateQuotationInternalRemarkOnly } from '#/modules/crmMgmt/quotation/helper/quotationStatus';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  quotationDetailQueryKey,
  quotationListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const HEADERS = ['#', '料號', '品名', '數量', '單位', '單價', '列金額', '客戶備註', '內部備註', ''];

function Cell({ children, className }) {
  return <td className={`px-2 py-1.5 ${className ?? ''}`}>{children}</td>;
}

Cell.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

function RemarkButton({ show, label, onClick }) {
  if (!show) {
    return null;
  }
  return (
    <Button type="button" variant="ghost" size="icon-sm" aria-label={`編輯${label}內部備註`} onClick={onClick}>
      <FilePenLine className="size-4" aria-hidden />
    </Button>
  );
}

RemarkButton.propTypes = {
  show: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * 報價項目（唯讀）：主項目＋縮排子項目；列金額 null 代表不完整、不計價。
 * 已發出／過期時可逐列改內部備註（PATCH line_updates）。
 */
const QuotationLinesSection = ({ detail, onRefetch }) => {
  const queryClient = useQueryClient();
  const canUpdate = useCan(PERM.QUOTATION_UPDATE);
  const [target, setTarget] = useState(null);
  const lines = detail.lines ?? [];
  const showRemarkEdit = canUpdate && canUpdateQuotationInternalRemarkOnly(detail.status);

  const remarkMutation = useMutation({
    retry: false,
    mutationFn: (internalRemark) => updateQuotation(
      detail.id,
      buildQuotationInternalRemarkPatch(detail.record?.updatedAt, { ...target, internalRemark }),
    ),
    onSuccess: async (result) => {
      toast.success(result?.message || '內部備註已更新');
      setTarget(null);
      if (result?.data) {
        queryClient.setQueryData(quotationDetailQueryKey(detail.id), result);
      }
      await queryClient.invalidateQueries({ queryKey: quotationListQueryKeyPrefix() });
    },
    onError: async (error) => {
      toast.error(getQuotationApiErrorMessage(error, '更新內部備註失敗'));
      if (isQuotationVersionConflict(error)) {
        await onRefetch?.();
      }
    },
  });

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <h2 className="text-base font-semibold text-gray-900">報價項目</h2>
        {lines.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">尚無項目。</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[56rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  {HEADERS.map((header) => (
                    <th key={header} className="px-2 py-1.5 font-medium whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {lines.flatMap((line, index) => [
                  <tr key={line.lineId}>
                    <Cell className="tabular-nums text-gray-500">{index + 1}</Cell>
                    <Cell className="whitespace-nowrap">{line.productNo || '-'}</Cell>
                    <Cell className="max-w-64">
                      <TruncatedTextWithTooltip text={line.itemName || '-'} />
                    </Cell>
                    <Cell className="text-right tabular-nums">{formatQuotationDecimal(line.qty)}</Cell>
                    <Cell>{line.unitName || '-'}</Cell>
                    <Cell className="text-right tabular-nums">{formatQuotationDecimal(line.unitPrice)}</Cell>
                    <Cell className="text-right tabular-nums">
                      {line.lineAmount == null
                        ? <span className="text-xs text-amber-700">未完整</span>
                        : formatQuotationDecimal(line.lineAmount)}
                    </Cell>
                    <Cell className="max-w-48">
                      <TruncatedTextWithTooltip text={line.customerRemark || '-'} />
                    </Cell>
                    <Cell className="max-w-48">
                      <TruncatedTextWithTooltip text={line.internalRemark || '-'} />
                    </Cell>
                    <Cell className="text-right">
                      <RemarkButton
                        show={showRemarkEdit}
                        label={`第 ${index + 1} 項`}
                        onClick={() => setTarget({
                          lineId: line.lineId,
                          internalRemark: line.internalRemark,
                          label: `第 ${index + 1} 項 ${line.itemName ?? ''}`,
                        })}
                      />
                    </Cell>
                  </tr>,
                  ...(line.subLines ?? []).map((subLine) => (
                    <tr key={subLine.subLineId} className="bg-slate-50/70 text-gray-700">
                      <Cell className="text-gray-400">
                        <CornerDownRight className="ml-auto size-3.5" aria-hidden />
                      </Cell>
                      <Cell className="whitespace-nowrap">{subLine.productNo || '-'}</Cell>
                      <Cell className="max-w-64">
                        <TruncatedTextWithTooltip text={subLine.itemName || '-'} />
                      </Cell>
                      <Cell className="text-right tabular-nums">{formatQuotationDecimal(subLine.qty)}</Cell>
                      <Cell>{subLine.unitName || '-'}</Cell>
                      <Cell className="text-xs text-muted-foreground">不計價</Cell>
                      <Cell />
                      <Cell className="max-w-48">
                        <TruncatedTextWithTooltip text={subLine.customerRemark || '-'} />
                      </Cell>
                      <Cell className="max-w-48">
                        <TruncatedTextWithTooltip text={subLine.internalRemark || '-'} />
                      </Cell>
                      <Cell className="text-right">
                        <RemarkButton
                          show={showRemarkEdit}
                          label={`第 ${index + 1} 項子項目`}
                          onClick={() => setTarget({
                            lineId: line.lineId,
                            subLineId: subLine.subLineId,
                            internalRemark: subLine.internalRemark,
                            label: `第 ${index + 1} 項子項目 ${subLine.itemName ?? ''}`,
                          })}
                        />
                      </Cell>
                    </tr>
                  )),
                ])}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {target ? (
        <QuotationInternalRemarkDialog
          key={`${target.lineId}-${target.subLineId ?? ''}`}
          open
          onOpenChange={(open) => { if (!open) setTarget(null); }}
          target={target}
          isPending={remarkMutation.isPending}
          onConfirm={(remark) => remarkMutation.mutate(remark)}
        />
      ) : null}
    </div>
  );
};

QuotationLinesSection.propTypes = {
  detail: PropTypes.object.isRequired,
  onRefetch: PropTypes.func,
};

export default QuotationLinesSection;
